// R-131-FIN-DECISION-F — risk dimension heatmap with per-pathway markers.

import React, { useMemo } from 'react';
import type { Pathway } from '../../../lib/finDecision/types';

export interface PathwayRiskMatrixProps {
  pathways: Pathway[];
}

type RiskDimension = 'fuel-price' | 'regulatory' | 'technology' | 'construction' | 'queue';

const DIMENSIONS: { id: RiskDimension; label: string }[] = [
  { id: 'fuel-price', label: 'Fuel price' },
  { id: 'regulatory', label: 'Regulatory' },
  { id: 'technology', label: 'Technology' },
  { id: 'construction', label: 'Construction' },
  { id: 'queue', label: 'Queue' },
];

const LEVELS = ['low', 'medium', 'high'] as const;

const LEVEL_INDEX: Record<string, number> = {
  none: 0,
  low: 0,
  mature: 0,
  'commercial-but-evolving': 1,
  medium: 1,
  high: 2,
  'first-of-kind': 2,
};

// Aurora categorical palette for per-pathway markers. These reference the
// theme chart tokens (var(--chart-1..4)) so the dots flip with light/dark and
// stay consistent with the sibling cashflow/band charts. NOT data — purely the
// per-pathway colour key.
const DOT_COLOR_VARS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
] as const;

function scoreFuel(pathway: Pathway): number {
  return LEVEL_INDEX[pathway.risk.fuelPriceRisk] ?? 1;
}

function scoreRegulatory(pathway: Pathway): number {
  return LEVEL_INDEX[pathway.risk.regulatoryRisk] ?? 1;
}

function scoreTechnology(pathway: Pathway): number {
  return LEVEL_INDEX[pathway.risk.technologyMaturity] ?? 1;
}

function scoreConstruction(pathway: Pathway): number {
  const constructionMonths = pathway.phases
    .filter((p) => p.kind === 'construction')
    .reduce((sum, p) => sum + p.durationMonths, 0);
  if (constructionMonths >= 60) return 2;
  if (constructionMonths >= 36) return 1;
  return 0;
}

function scoreQueue(pathway: Pathway): number {
  const queue = pathway.risk.queueDelayDistribution;
  if (!queue) return pathway.technology === 'grid-tariff' ? 2 : 0;
  if (queue.p90Years >= 6) return 2;
  if (queue.medianYears >= 3.5) return 1;
  return 0;
}

function dimensionScore(pathway: Pathway, dimension: RiskDimension): number {
  switch (dimension) {
    case 'fuel-price':
      return scoreFuel(pathway);
    case 'regulatory':
      return scoreRegulatory(pathway);
    case 'technology':
      return scoreTechnology(pathway);
    case 'construction':
      return scoreConstruction(pathway);
    case 'queue':
      return scoreQueue(pathway);
    default:
      return 1;
  }
}

// Aurora subtle status tints. These tokens FLIP per theme (dark-appropriate
// tint in dark mode, light wash in light mode) — see :root / .light in
// index.css. Returned as a CSS background value (not a Tailwind class) so the
// heat tint tracks the theme instead of being a fixed light pastel.
//   high (level 2)   -> danger-subtle
//   medium (level 1) -> warning-subtle
//   low (level 0)    -> success-subtle
function cellHeatBg(level: number): string {
  if (level >= 2) return 'var(--danger-subtle)';
  if (level >= 1) return 'var(--warning-subtle)';
  return 'var(--success-subtle)';
}

// Legend label: full pathway name, no truncation. The legend row is a
// flex-wrap container so long names wrap onto the next line rather than being
// clipped to "Hybrid: Ga…" (the in-cell dots carry the same name via `title`).
function legendLabel(pathway: Pathway, index: number): string {
  const prefix = String.fromCharCode(65 + index);
  return `${prefix}: ${pathway.name}`;
}

export function PathwayRiskMatrix({ pathways }: PathwayRiskMatrixProps) {
  const grid = useMemo(() => {
    return DIMENSIONS.map((dim) => ({
      ...dim,
      cells: LEVELS.map((level, levelIdx) => ({
        level,
        levelIdx,
        pathways: pathways
          .map((pathway, pathwayIdx) => ({
            pathway,
            pathwayIdx,
            score: dimensionScore(pathway, dim.id),
          }))
          .filter((entry) => entry.score === levelIdx),
      })),
    }));
  }, [pathways]);

  if (pathways.length === 0) return null;

  return (
    <div
      className="rounded-lg border border-border bg-surface-1 p-4"
      data-testid="pathway-risk-matrix"
    >
      <h3 className="text-sm font-semibold text-text-primary mb-3">Risk matrix</h3>
      <div className="overflow-x-auto">
        {/* border-separate + spacing: the heat-tint cells read as discrete
            rounded tiles separated by the dark canvas — replaces the grey
            border-t row dividers that rendered muddy over the dark
            success/warning/danger heat washes in dark mode (#121). */}
        <table className="min-w-full text-xs border-separate border-spacing-x-1.5 border-spacing-y-2">
          <thead>
            <tr className="text-text-secondary">
              <th className="px-2 py-1 text-left font-medium">Dimension</th>
              {LEVELS.map((level) => (
                <th key={level} className="px-2 py-1 text-center font-medium capitalize">
                  {level}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.map((row) => (
              <tr key={row.id}>
                <td className="px-2 py-2 font-medium text-text-primary">{row.label}</td>
                {row.cells.map((cell) => (
                  <td
                    key={`${row.id}-${cell.level}`}
                    className="px-2 py-3 text-center relative min-w-[88px]"
                    style={{
                      backgroundColor: cellHeatBg(cell.levelIdx),
                      borderRadius: '0.375rem',
                      backgroundClip: 'padding-box',
                    }}
                  >
                    <div className="flex flex-wrap items-center justify-center gap-1 min-h-[20px]">
                      {cell.pathways.map(({ pathway, pathwayIdx }) => (
                        <span
                          key={pathway.id}
                          className="inline-block w-2.5 h-2.5 rounded-full shadow-sm"
                          style={{
                            backgroundColor: DOT_COLOR_VARS[pathwayIdx % DOT_COLOR_VARS.length],
                            border: '1px solid var(--surface-1)',
                          }}
                          title={pathway.name}
                          data-testid={`risk-dot-${pathway.id}-${row.id}`}
                        />
                      ))}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-text-secondary">
        {pathways.map((pathway, index) => (
          <span key={pathway.id} className="inline-flex items-center gap-1">
            <span
              className="inline-block w-2.5 h-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: DOT_COLOR_VARS[index % DOT_COLOR_VARS.length] }}
            />
            {legendLabel(pathway, index)}
          </span>
        ))}
      </div>
    </div>
  );
}
